/**
 * SCRUM expert criteria for sprint health calculation.
 * 
 * Logic based on:
 * 1. Progress vs. Time elapsed (Burn-down trend)
 * 2. Obstacles (Urgent tickets in To Do/Bug Fixing)
 * 3. Pressure (WIP in Bug Fixing column)
 */

export const calculateSprintHealth = (sprint, ticketsOrStats, t) => {
  if (!sprint || !ticketsOrStats) {
    return { status: 'green', message: t('sprintStarting') || 'Sprint en inicio' };
  }

  // Support both legacy array and new stats object
  const isStatsObj = !Array.isArray(ticketsOrStats);
  const totalTickets = isStatsObj ? ticketsOrStats.totalTickets : ticketsOrStats.length;
  
  if (totalTickets === 0) {
    return { status: 'green', message: t('sprintStarting') || 'Sprint en inicio' };
  }

  // 1. Time metrics
  const start = new Date(sprint.startDate);
  const end = new Date(sprint.endDate);
  const now = new Date();
  const totalDuration = end - start;
  const elapsed = now - start;
  
  const timeElapsedPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

  // 2. Progress metrics
  const doneTickets = isStatsObj ? (ticketsOrStats.columnCounts?.done || 0) : ticketsOrStats.filter(t => t.status === 'done').length;
  const progressPercent = (doneTickets / totalTickets) * 100;

  // 3. Risk factors
  const urgentPending = isStatsObj ? (ticketsOrStats.urgentPending || 0) : ticketsOrStats.filter(t => 
    (t.priority === 'urgent' || t.priority === 'urgente') && (t.status === 'todo' || t.status === 'bug_fixing' || t.status === 'pending')
  ).length;

  const bugFixingCount = isStatsObj ? (ticketsOrStats.columnCounts?.bug_fixing || 0) : ticketsOrStats.filter(t => t.status === 'bug_fixing' || t.status === 'pending').length;
  const bugFixingThreshold = Math.max(3, Math.ceil(totalTickets * 0.2)); // 20% of tickets or at least 3

  // 4. Expert Status Determination
  let status = 'green';
  let reasons = [];

  // Critical checks (RED)
  if (timeElapsedPercent > 30 && progressPercent < (timeElapsedPercent * 0.4)) {
    status = 'red';
    reasons.push(t('healthBehindSchedule') || 'Muy atrasado respecto al tiempo');
  }
  if (urgentPending >= 3) {
    status = 'red';
    reasons.push(t('healthTooManyUrgent') || 'Demasiados tickets urgentes pendientes');
  }
  if (bugFixingCount > bugFixingThreshold * 1.5) {
    status = 'red';
    reasons.push(t('healthBugOverflow') || 'Sobrecarga en corrección de errores');
  }

  // Warning checks (YELLOW) - Only if not already RED
  if (status !== 'red') {
    if (progressPercent < (timeElapsedPercent * 0.7)) {
      status = 'yellow';
      reasons.push(t('healthSlightlyBehind') || 'Ligero retraso en el progreso');
    }
    if (urgentPending > 0 && urgentPending < 3) {
      status = 'yellow';
      reasons.push(t('healthUrgentPending') || 'Tickets urgentes por atender');
    }
    if (bugFixingCount > bugFixingThreshold) {
      status = 'yellow';
      reasons.push(t('healthHighWipBugs') || 'WIP alto en bugs');
    }
  }

  // Final message
  let message = '';
  if (status === 'green') {
    message = t('healthHealthy') || 'Sprint saludable y en ritmo';
  } else {
    message = reasons.join(' • ');
  }

  return { status, message, progressPercent, timeElapsedPercent };
};
